// Hero.js
import { StaticImage } from 'gatsby-plugin-image'
import React from 'react'
import { motion } from 'framer-motion'

const Hero = () => {
  // Animation variants
  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  }

  const staggerChildren = {
    animate: {
      transition: {
        staggerChildren: 0.2
      }
    }
  }

  return (
    <div className="relative mt-[12vh] bg-white overflow-hidden">
      {/* Decorative Elements */}
      <div className="absolute inset-0 z-0">
        <div className="absolute top-20 left-10 w-72 h-72 bg-indigo-100 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob" />
        <div className="absolute top-40 right-10 w-72 h-72 bg-pink-100 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-2000" />
        <div className="absolute -bottom-8 left-20 w-72 h-72 bg-purple-100 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-4000" />
      </div>

      <div className="container mx-auto relative z-10">
        <motion.div
          initial="initial"
          animate="animate"
          variants={staggerChildren}
          className="px-4 sm:px-6 lg:px-8 py-12 md:py-20"
        >
          <div className="flex flex-col md:flex-row items-center justify-between gap-12">
            {/* Left Content */}
            <motion.div
              className="w-full md:w-1/2 space-y-8"
              variants={fadeInUp}
            >
              {/* Rating Badge */}
              <motion.div
                className="inline-flex items-center bg-indigo-50 rounded-full px-6 py-2.5 shadow-sm"
                whileHover={{ scale: 1.05 }}
              >
                <span className="text-indigo-600 text-sm font-medium flex items-center gap-2">
                  🏆 Your Trusted Job Hunt Companion
                </span>
              </motion.div>

              {/* Main Heading */}
              <motion.h1
                className="text-3xl md:text-5xl font-bold text-neutral-900 tracking-tight leading-tight"
                variants={fadeInUp}
              >
                Create <span className="text-indigo-600">Stand-Out</span> Resumes with AI Magic
              </motion.h1>

              {/* Subheading */}
              <motion.p
                className="text-md text-neutral-700 max-w-2xl leading-relaxed"
                variants={fadeInUp}
              >
                Land your dream job with our AI-powered resume builder. Create professionally designed,
                keyword-optimized resumes that boost your interview chances by 3x. Perfect for any industry
                or experience level.
              </motion.p>

              {/* Stats */}
              <motion.div
                className="grid grid-cols-3 gap-6 py-6"
                variants={fadeInUp}
              >
                {[
                  { number: "Unlimited", label: "Resume / Cover / Email" },
                  { number: "98%", label: "ATS Success Rate" },
                  { number: "24/7", label: "Support" }
                ].map((stat, index) => (
                  <div key={index} className="text-center">
                    <div className="text-2xl font-bold text-indigo-600">{stat.number}</div>
                    <div className="text-sm text-neutral-600">{stat.label}</div>
                  </div>
                ))}
              </motion.div>

              {/* CTA Buttons */}
              <motion.div
                className="flex flex-col sm:flex-row gap-4"
                variants={fadeInUp}
              >
                <motion.button
                  className="bg-indigo-600 text-white px-8 py-4 rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors shadow-lg"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => window.open("https://app.resumeflex.com", "_blank")}
                >
                  Start Building
                </motion.button>
                <motion.button
                  className="bg-white text-neutral-700 px-8 py-4 rounded-lg text-sm font-medium border border-neutral-200 hover:bg-neutral-50 transition-colors"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => window.open("https://youtu.be/FYnZETIEKEQ", "_blank")}
                >
                  Watch Demo ▶
                </motion.button>
              </motion.div>

              {/* Feature Points */}
              <motion.div
                className="flex flex-wrap gap-6"
                variants={fadeInUp}
              >
                {[
                  "AI-Powered Templates",
                  "ATS-Friendly",
                  "Real-Time Preview",
                  "Export to Multiple Formats"
                ].map((feature, index) => (
                  <div key={index} className="flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-sm">
                    <svg className="w-5 h-5 text-indigo-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-sm text-neutral-700">{feature}</span>
                  </div>
                ))}
              </motion.div>
            </motion.div>

            {/* Right Content - Mock UI */}
            <motion.div
              className="w-full md:w-1/2"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-indigo-500 to-purple-500 blur-2xl opacity-20 rounded-xl" />
                <div className="relative bg-white/80 backdrop-blur-sm rounded-xl shadow-2xl p-2">
                  <StaticImage
                    src="../images/product-screenshot.png"
                    alt="ResumeFlex AI-powered resume creation"
                    placeholder="blurred"
                    layout="fullWidth"
                    className="rounded-lg"
                    quality={100}
                  />
                </div>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </div>
  )
}

export default Hero
